import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace, WORKSPACES } from "@/context/WorkspaceContext";
import {
  LayoutDashboard,
  Receipt,
  Paintbrush,
  Truck,
  ClipboardList,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  Hammer,
  Tags,
  Search,
  CreditCard,
  Users as UsersIcon,
  KeyRound,
  Sparkles,
  FileText,
  ReceiptText,
  Refrigerator,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { GlobalSearch, GlobalSearchTrigger } from "@/components/GlobalSearch";
import GlobalSyncStatus from "@/components/GlobalSyncStatus";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import TutorialTour from "@/components/TutorialTour";
import { CURRENT_TUTORIAL_VERSION } from "@/components/tutorial/tutorialSteps";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CABINET_NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", end: true },
  { to: "/orders", icon: Receipt, label: "Orders", testid: "nav-orders" },
  { to: "/paint", icon: Paintbrush, label: "Paint Board", testid: "nav-paint" },
  // Deliveries hidden until the real Base44-driven workflow lands.
  // { to: "/deliveries", icon: Truck, label: "Deliveries", testid: "nav-deliveries" },
  { to: "/manifests", icon: ClipboardList, label: "Manifests", testid: "nav-manifests" },
  { to: "/catalog", icon: Tags, label: "Cost Catalog", testid: "nav-catalog" },
  { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
  { to: "/stripe", icon: CreditCard, label: "Stripe", testid: "nav-stripe" },
  { to: "/team", icon: UsersIcon, label: "Team", testid: "nav-team", adminOnly: true },
  { to: "/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
];

const APPLIANCE_NAV = [
  { to: "/appliance", icon: LayoutDashboard, label: "Dashboard", testid: "nav-appliance-dashboard", end: true },
  { to: "/appliance/documents", icon: FileText, label: "Documents", testid: "nav-appliance-documents" },
  { to: "/appliance/report", icon: ReceiptText, label: "Daily Report", testid: "nav-appliance-report" },
  { to: "/settings", icon: Settings, label: "Settings", testid: "nav-appliance-settings" },
];

const WORKSPACE_ICON = { cabinet: Hammer, appliance: Refrigerator };

function WorkspaceSwitcher({ onItemClick }) {
  const { workspace, setWorkspace } = useWorkspace();
  const nav = useNavigate();
  const Icon = WORKSPACE_ICON[workspace] || Hammer;
  const meta = WORKSPACES[workspace];

  const choose = (id) => {
    setWorkspace(id);
    nav(WORKSPACES[id].home);
    onItemClick?.();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="workspace-switcher"
          className="w-full h-16 flex items-center gap-3 px-5 border-b border-border/70 hover:bg-secondary/50 transition-colors text-left"
        >
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="leading-tight min-w-0 flex-1">
            <div className="font-heading font-bold text-[15px] truncate">{meta.name}</div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">{meta.tagline}</div>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[248px]">
        {Object.values(WORKSPACES).map((w) => {
          const WIcon = WORKSPACE_ICON[w.id] || Hammer;
          const active = w.id === workspace;
          return (
            <DropdownMenuItem
              key={w.id}
              data-testid={`workspace-option-${w.id}`}
              onClick={() => choose(w.id)}
              className="gap-3 py-2.5 cursor-pointer"
            >
              <div className="w-7 h-7 rounded bg-secondary grid place-items-center text-primary shrink-0">
                <WIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{w.name}</div>
                <div className="text-[10px] tracking-[0.12em] uppercase text-muted-foreground">{w.tagline}</div>
              </div>
              {active && <Check className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { workspace, setWorkspace, meta } = useWorkspace();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourForceFull, setTourForceFull] = useState(false);

  // Keep the active workspace in sync with the current route so the sidebar
  // nav + branding always match the page — including on direct URL loads.
  // `/settings` is shared by both workspaces, so it never forces a switch.
  useEffect(() => {
    const isAppliance = pathname === "/appliance" || pathname.startsWith("/appliance/");
    const isShared = pathname === "/settings";
    if (isAppliance && workspace !== "appliance") setWorkspace("appliance");
    else if (!isAppliance && !isShared && workspace !== "cabinet") setWorkspace("cabinet");
  }, [pathname, workspace, setWorkspace]);

  const navItems = workspace === "appliance" ? APPLIANCE_NAV : CABINET_NAV;
  const WorkspaceIcon = WORKSPACE_ICON[workspace] || Hammer;

  // Auto-launch the tutorial. Two cases:
  //  1. First-time viewer (`tutorial_completed_at` is null) → full onboarding tour
  //  2. Existing user whose `tutorial_version` is below CURRENT_TUTORIAL_VERSION
  //     → short "What's new" mini-tour showing only steps introduced since
  //       their last completion. We surface it after login exactly once —
  //       when the user marks it done, tutorial_version bumps and it stops
  //       reappearing until we ship yet another version.
  useEffect(() => {
    if (!user) return;
    const notCompleted = !user.tutorial_completed_at;
    const outOfDate =
      user.tutorial_completed_at &&
      (user.tutorial_version || 0) < CURRENT_TUTORIAL_VERSION;
    if (notCompleted || outOfDate) setTourOpen(true);
  }, [user]);

  // Anyone can request the tour manually from Settings via a custom event.
  // Replay always shows the FULL tour, regardless of version — otherwise
  // users who are up-to-date would click Replay and see nothing.
  useEffect(() => {
    const onReplay = () => {
      setTourForceFull(true);
      setTourOpen(true);
    };
    window.addEventListener("cd:replay-tutorial", onReplay);
    return () => window.removeEventListener("cd:replay-tutorial", onReplay);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F4F3EF]">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border/70 bg-white">
        <SidebarContent navItems={navItems} initials={initials} user={user} onLogout={handleLogout} onChangePassword={() => setPasswordOpen(true)} />
      </aside>

      {/* Sidebar - mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-border/70 flex flex-col animate-fade-in">
            <SidebarContent
              navItems={navItems}
              initials={initials}
              user={user}
              onLogout={handleLogout}
              onChangePassword={() => { setMobileOpen(false); setPasswordOpen(true); }}
              onItemClick={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-border/70 bg-white">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-md hover:bg-secondary"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <WorkspaceIcon className="w-4 h-4" />
            </div>
            <span className="font-heading font-bold">{meta.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <GlobalSyncStatus variant="mobile" />
            <button
              data-testid="mobile-search-btn"
              onClick={() => setSearchOpen(true)}
              className="p-2 -mr-2 rounded-md hover:bg-secondary"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Desktop top bar with search */}
        <header className="hidden lg:flex items-center justify-end gap-3 px-8 h-16 border-b border-border/70 bg-white">
          <GlobalSyncStatus />
          <GlobalSearchTrigger onOpen={() => setSearchOpen(true)} />
        </header>

        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
      <TutorialTour
        open={tourOpen}
        forceFull={tourForceFull}
        onClose={() => {
          setTourOpen(false);
          setTourForceFull(false);
        }}
      />
    </div>
  );
}

function SidebarContent({ navItems, initials, user, onLogout, onChangePassword, onItemClick }) {
  const isAdmin = user?.role === "admin";
  const items = navItems.filter((i) => !i.adminOnly || isAdmin);
  return (
    <>
      <WorkspaceSwitcher onItemClick={onItemClick} />

      <nav className="flex-1 px-3 py-5 space-y-1">
        <div className="label-eyebrow px-3 pb-2">Workspace</div>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={onItemClick}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-foreground/80 hover:bg-secondary hover:text-foreground",
              )
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={1.75} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border/70 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-secondary grid place-items-center font-semibold text-sm text-primary">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || "User"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="change-password-btn"
          onClick={onChangePassword}
          className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-md border border-border hover:bg-secondary transition-colors mb-2"
        >
          <KeyRound className="w-4 h-4" /> Change password
        </button>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-md border border-border hover:bg-secondary transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </>
  );
}
